import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSEO } from "@/hooks/useSEO";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, ArrowLeft, Plus, Check, BookOpen, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { MermaidDiagram } from "@/components/ui/MermaidDiagram";
import { ResponsiveTable } from "@/components/ui/ResponsiveTable";
import { getSectionIcon } from "@/lib/sectionIcons";
import { prepareMathContent } from "@/lib/mathUtils";

interface TemplateSheet {
  id: string;
  slug: string;
  category: string;
  difficulty: string;
  title_en: string;
  title_fr: string;
  description_en: string;
  description_fr: string;
  content_en: string;
  content_fr: string;
  tags: string[];
}

export default function TemplateSheetDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const [sheet, setSheet] = useState<TemplateSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const fetch = async () => {
      const { data, error } = await supabase
        .from("template_sheets" as any)
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (!error && data) setSheet(data as any);
      setLoading(false);
    };
    fetch();
  }, [slug]);

  const title = sheet ? (lang === "fr" ? sheet.title_fr : sheet.title_en) : "";
  const description = sheet ? (lang === "fr" ? sheet.description_fr : sheet.description_en) : "";
  const content = sheet ? (lang === "fr" ? sheet.content_fr : sheet.content_en) : "";

  useSEO({
    title: sheet ? `${title} – Free Revision Sheet | System Atlas` : "Loading... | System Atlas",
    description: sheet ? `${description}. Free, structured revision sheet on System Atlas. Study, quiz yourself, and track your progress.` : "",
    noIndex: !loading && !sheet,
    alternates: sheet ? [
      { hreflang: "en", href: `https://systematlas.fr/learn/sheets/${sheet.slug}` },
      { hreflang: "fr", href: `https://systematlas.fr/fr/learn/sheets/${sheet.slug}` },
      { hreflang: "x-default", href: `https://systematlas.fr/learn/sheets/${sheet.slug}` },
    ] : [],
  });

  const handleAdd = async () => {
    if (!user) { navigate("/sign-in"); return; }
    if (!sheet) return;
    setAdding(true);
    const { error } = await supabase.from("revision_sheets").insert({
      user_id: user.id,
      content: lang === "fr" ? sheet.content_fr : sheet.content_en,
      title: lang === "fr" ? sheet.title_fr : sheet.title_en,
      source_type: "template",
      understanding_status: "new",
    });
    if (error) {
      toast.error(t("templates.addError", "Failed to add sheet"));
    } else {
      setAdded(true);
      toast.success(t("templates.addSuccess", "Sheet added to My Sheets!"));
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!sheet) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">{t("templates.notFound", "Sheet not found")}</h1>
        <Link to="/learn/sheets" className="text-primary underline">{t("templates.backToLibrary", "Back to library")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {sheet && (
        <ArticleJsonLd
          title={title}
          description={description}
          publishedDate="2025-01-01"
          modifiedDate="2026-03-27"
          inLanguage={lang}
        />
      )}

      {/* JSON-LD LearningResource */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            name: title,
            description,
            educationalLevel: sheet.difficulty === "advanced" ? "Advanced" : sheet.difficulty === "introductory" ? "Beginner" : "Intermediate",
            inLanguage: lang,
            teaches: sheet.tags.join(", "),
            provider: { "@type": "Organization", name: "System Atlas", url: "https://systematlas.fr" },
            url: `https://systematlas.fr/${lang === "fr" ? "fr/" : ""}learn/sheets/${sheet.slug}`,
          }),
        }}
      />

      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6 flex-wrap">
        <Link to="/learn" className="hover:text-foreground">{t("nav.learn", "Learn")}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/learn/sheets" className="hover:text-foreground">{t("templates.title", "Sheet Library")}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-foreground truncate max-w-[200px]">{title}</span>
      </nav>

      {/* Title + CTA */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>{title}</h1>
          <p className="text-muted-foreground max-w-xl">{description}</p>
          {sheet.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {sheet.tags.map(tag => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <Button
          className="gap-2 shrink-0"
          onClick={handleAdd}
          disabled={adding || added}
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : added ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {added ? t("templates.added", "Added!") : t("templates.addToSheets", "Add to My Sheets")}
        </Button>
      </div>

      {/* Sheet content */}
      <div className="border border-border rounded-xl bg-card p-6 sm:p-8">
        <div className="revision-prose max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeKatex]}
            components={{
              table: ({ children }) => <ResponsiveTable>{children}</ResponsiveTable>,
              h2({ children }) {
                const text = String(children);
                const Icon = getSectionIcon(text);
                return (
                  <h2 className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </span>
                    {children}
                  </h2>
                );
              },
              code({ className, children, ...props }) {
                const c = String(children).trim();
                if (/language-mermaid/.test(className || "") || /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|mindmap|gitgraph)\b/.test(c)) {
                  return <MermaidDiagram chart={c} />;
                }
                return <code className={className} {...props}>{children}</code>;
              },
            }}
          >
            {prepareMathContent(content)}
          </ReactMarkdown>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-xl bg-muted/30 border border-border">
        <div>
          <h3 className="font-semibold mb-1">{t("templates.ctaTitle", "Want more sheets?")}</h3>
          <p className="text-sm text-muted-foreground">{t("templates.ctaDesc", "Browse the full library or create your own from any PDF or notes.")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/learn/sheets">{t("templates.browseAll", "Browse Library")}</Link>
          </Button>
          <Button asChild>
            <Link to="/">{t("templates.createOwn", "Create Sheet")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
